import {
  AntdBreadcrumb,
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Icons,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Spin,
  Space,
  Table,
  TextField,
  Typography,
  useSelect,
  useTable,
} from "@pankod/refine-antd";
import {
  HttpError,
  LogicalFilter,
  useCreate,
  useDelete,
  useNotification,
  useUpdate,
} from "@pankod/refine-core";
import { IHealthInsurer } from "interfaces";
import { IPartner } from "interfaces/partner";
import { FC, useState } from "react";
import {
  PartnerSellerForm,
  PartnerSellerFormValues,
} from "components/forms/frm_partner_seller";
import { extractApiErrorMessage } from "utils/utils";

import { CommercialConditionsModal } from "./mdl_commercial_conditions";

type PartnerSeller = {
  _id?: string;
  id?: string;
  seller_code?: string;
  seller_name?: string;
  health_insurer_name?: string;
  health_insurer_id?: string | number;
  partner_code?: string;
  active?: boolean;
  default_seller?: boolean;
};

type PartnerSellersModalProps = {
  isOpen: boolean;
  partner: IPartner | null;
  onClose: () => void;
};

type PartnerSellerFilter = {
  seller_code?: string;
  health_insurer_id?: string | number;
};

type HealthInsurerOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

const MAX_SELLER_NAME_LENGTH = 100;

export const PartnerSellersModal: FC<PartnerSellersModalProps> = ({
  isOpen,
  partner,
  onClose,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<PartnerSeller | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [conditionsSeller, setConditionsSeller] = useState<PartnerSeller | null>(
    null
  );
  const [createForm] = Form.useForm<PartnerSellerFormValues>();
  const { open: openNotification } = useNotification();

  const { mutate: createSeller, isLoading: isCreating } = useCreate<
    PartnerSeller,
    HttpError,
    PartnerSeller
  >();
  const { mutate: updateSeller, isLoading: isUpdatingSeller } = useUpdate<
    PartnerSeller,
    HttpError,
    PartnerSeller
  >();
  const { mutate: deleteSeller, isLoading: isDeletingSeller } = useDelete<
    PartnerSeller,
    HttpError,
    PartnerSeller
  >();

  const { tableProps, searchFormProps, tableQueryResult } = useTable<
    PartnerSeller,
    HttpError,
    PartnerSellerFilter
  >({
    resource: "v1/partner-sellers",
    initialCurrent: 1,
    initialPageSize: 5,
    hasPagination: true,
    queryOptions: { enabled: !!partner?.partner_code && isOpen },
    metaData: partner?.partner_code
      ? { URLSuffix: `partner/${partner.partner_code}` }
      : undefined,
    onSearch: (values) =>
      Object.entries(values).reduce<LogicalFilter[]>(
        (filters, [field, value]) => [
          ...filters,
          {
            field,
            operator: "eq",
            value,
          },
        ],
        []
      ),
  });

  const { selectProps: healthInsurerSelectProps } = useSelect<IHealthInsurer>({
    resource: "v1/health-insurer",
    optionLabel: "Description",
    optionValue: "HealthInsurerId",
    hasPagination: true,
  });

  const healthInsurerOptions: HealthInsurerOption[] = (
    healthInsurerSelectProps.options ?? []
  ).map(
    (option): HealthInsurerOption => ({
      value: String(option.value ?? ""),
      label: String(option.label ?? option.children ?? ""),
      disabled: option.disabled,
    })
  );

  const healthInsurerSelectPropsTyped = {
    ...healthInsurerSelectProps,
    options: healthInsurerOptions,
  };

  const isTableLoading = Boolean(
    tableProps.loading || tableQueryResult?.isFetching
  );

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    searchFormProps.form?.submit();
  };

  const handleClose = () => {
    setDrawerOpen(false);
    handleCleanFilters();
    onClose();
  };

  const getSellerId = (record: PartnerSeller): string | null => {
    const id = record._id ?? record.id ?? record.seller_code;
    return id ? String(id) : null;
  };

  const parseHealthInsurerId = (
    value?: string | number
  ): string | number | undefined => {
    if (value === undefined) {
      return undefined;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  };

  const refetchSellers = () => tableQueryResult?.refetch();

  const sanitizeText = (value?: string) => value?.trim() ?? "";

  const buildSellerPayload = (
    data: Partial<PartnerSeller>
  ): PartnerSeller | null => {
    const sellerCode = sanitizeText(data.seller_code);
    const sellerName = sanitizeText(data.seller_name);
    const hasHealthInsurer = Boolean(data.health_insurer_id);

    if (!sellerCode || !sellerName) {
      handleError(new Error("Preencha os campos obrigatórios"));
      return null;
    }

    if (sellerName.length > MAX_SELLER_NAME_LENGTH) {
      handleError(
        new Error(`Nome deve ter no máximo ${MAX_SELLER_NAME_LENGTH} caracteres`)
      );
      return null;
    }

    if (!hasHealthInsurer) {
      handleError(new Error("Selecione a operadora"));
      return null;
    }

    return {
      seller_code: sellerCode,
      seller_name: sellerName,
      health_insurer_id: parseHealthInsurerId(data.health_insurer_id),
      health_insurer_name: data.health_insurer_name ?? "",
      partner_code: data.partner_code,
      active: data.active,
    };
  };

  const getEditingHealthInsurerValue = (record: PartnerSeller) => {
    if (!editingValues?.health_insurer_id) {
      return undefined;
    }

    return {
      value: String(editingValues.health_insurer_id),
      label:
        editingValues.health_insurer_name ||
        record.health_insurer_name ||
        "-",
    };
  };

  const handleToggleSeller = (record: PartnerSeller, checked: boolean) => {
    const sellerId = getSellerId(record);
    if (!sellerId) {
      return;
    }

    updateSeller(
      {
        resource: "v1/partner-sellers",
        id: sellerId,
        values: {
          active: checked,
          partner_code: partner?.partner_code ?? record.partner_code,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          refetchSellers();
          openNotification?.({
            type: "success",
            description: "Sucesso!",
            message: checked
              ? "Vendedor habilitado com sucesso"
              : "Vendedor desabilitado com sucesso",
          });
        },
      }
    );
  };

  const handleSetDefaultSeller = (record: PartnerSeller) => {
    const sellerId = getSellerId(record);
    if (!sellerId || record.default_seller) {
      return;
    }

    updateSeller(
      {
        resource: "v1/partner-sellers",
        id: sellerId,
        values: {
          default_seller: true,
          partner_code: partner?.partner_code ?? record.partner_code,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          refetchSellers();
          openNotification?.({
            type: "success",
            description: "Sucesso!",
            message: "Vendedor definido como padrão do parceiro",
          });
        },
        onError: (error) =>
          handleError(error, "Erro ao definir vendedor padrão"),
      }
    );
  };

  const handleShowConditions = (record: PartnerSeller) => {
    setConditionsSeller(record);
  };

  const handleCloseConditions = () => {
    setConditionsSeller(null);
  };

  const handleEdit = (record: PartnerSeller) => {
    const sellerId = getSellerId(record);
    if (!sellerId) {
      return;
    }

    setEditingId(sellerId);
    setEditingValues({
      seller_code: record.seller_code ?? "",
      seller_name: record.seller_name ?? "",
      health_insurer_id: record.health_insurer_id
        ? String(record.health_insurer_id)
        : undefined,
      health_insurer_name: record.health_insurer_name,
      _id: record._id ?? record.id,
      active: record.active,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValues(null);
  };

  const handleError = (error: unknown, description?: string) => {
    openNotification?.({
      type: "error",
      description,
      message: extractApiErrorMessage(error),
    });
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingValues) {
      return;
    }

    const updatePayload = buildSellerPayload({
      seller_code: editingValues.seller_code,
      seller_name: editingValues.seller_name,
      health_insurer_id: editingValues.health_insurer_id,
      health_insurer_name: editingValues.health_insurer_name,
      partner_code: partner?.partner_code,
      active: editingValues.active,
    });

    if (!updatePayload) {
      return;
    }

    updateSeller(
      {
        resource: "v1/partner-sellers",
        id: editingId,
        values: updatePayload,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          refetchSellers();
          handleCancelEdit();
          openNotification?.({
            type: "success",
            description: "Sucesso!",
            message: "Vendedor editado com sucesso",
          });
        },
        onError: (error) =>
          handleError(error, "Erro ao salvar vendedor"),
      }
    );
  };

  const handleAddSeller = () => {
    createForm.resetFields();
    setDrawerOpen(true);
  };

  const handleCreateSeller = (values: PartnerSellerFormValues) => {
    const selectedHealthInsurer = healthInsurerOptions.find(
      (option) => option.value === values.health_insurer_id
    );

    const sellerPayload = buildSellerPayload({
      seller_code: values.seller_code,
      seller_name: values.seller_name,
      health_insurer_id: values.health_insurer_id,
      health_insurer_name: selectedHealthInsurer?.label ?? "",
      partner_code: partner?.partner_code,
      active: true,
    });

    if (!sellerPayload) {
      return;
    }

    createSeller(
      {
        resource: "v1/partner-sellers",
        metaData: { skipTrailingSlash: true },
        values: sellerPayload,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          createForm.resetFields();
          setDrawerOpen(false);
          refetchSellers();
          openNotification?.({
            type: "success",
            description: "Sucesso!",
            message: "Vendedor criado com sucesso",
          });
        },
        onError: (error) => handleError(error, "Erro ao salvar vendedor"),
      }
    );
  };

  const handleAfterDelete = () => {
    const pagination = tableProps.pagination;
    if (pagination && typeof pagination !== "boolean") {
      const currentPage = pagination.current ?? 1;
      const pageSize =
        pagination.pageSize ?? pagination.defaultPageSize ?? 10;
      const isLastItemOnPage =
        (tableProps.dataSource?.length ?? 0) <= 1 && currentPage > 1;

      if (isLastItemOnPage) {
        const newPage = currentPage - 1;
        pagination.onChange?.(newPage, pageSize);
        tableProps.onChange?.(
          { ...pagination, current: newPage },
          {},
          {},
          { action: "paginate", currentDataSource: [] }
        );
        refetchSellers();
        return;
      }
    }

    refetchSellers();

    openNotification?.({
      type: "success",
      description: "Sucesso!",
      message: "Vendedor removido com sucesso",
    });
  };

  const handleDelete = (record: PartnerSeller) => {
    const deleteId = getSellerId(record);
    if (!deleteId) {
      return;
    }

    deleteSeller(
      {
        resource: "v1/partner-sellers",
        id: deleteId,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: handleAfterDelete,
      }
    );
  };

  return (
    <>
      <Modal
        visible={isOpen}
        onCancel={handleClose}
        footer={null}
        width={1000}
        title="Vendedores"
        destroyOnClose
      >
        <Space
          align="center"
          style={{ width: "100%", justifyContent: "space-between" }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Configure os parâmetros dos vendedores
          </Typography.Title>
          <Button
            type="primary"
            icon={<Icons.PlusSquareOutlined />}
            onClick={handleAddSeller}
          >
            Adicionar
          </Button>
        </Space>

        <Card style={{ marginTop: 16 }} bodyStyle={{ paddingBottom: 8 }}>
          <Form {...searchFormProps} layout="vertical">
            <Row gutter={[16, 16]} style={{ width: "100%" }}>
              <Col xs={24} sm={12}>
                <Form.Item name="seller_code" label="Código">
                  <Input placeholder="Código" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="health_insurer_id" label="Operadora">
                  <Select
                    {...healthInsurerSelectPropsTyped}
                    allowClear
                    placeholder="Operadora"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
                <Space>
                  <Button
                    type="default"
                    icon={<Icons.ClearOutlined />}
                    onClick={handleCleanFilters}
                  >
                    Limpar
                  </Button>
                  <Button
                    type="primary"
                    icon={<Icons.SearchOutlined />}
                    htmlType="submit"
                  >
                    Buscar
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          {...tableProps}
          loading={isTableLoading}
          rowKey={(record) => getSellerId(record) ?? ""}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} registros`,
          }}
        >
          <Table.Column
            dataIndex="seller_code"
            key="seller_code"
            title="Código"
            render={(_, record: PartnerSeller) =>
              editingId ===
              (record._id ?? record.id ?? record.seller_code) ? (
                <Input
                  value={
                    editingValues?.seller_code
                      ? String(editingValues.seller_code)
                      : ""
                  }
                  onChange={(e) =>
                    setEditingValues((prev) =>
                      prev ? { ...prev, seller_code: e.target.value } : prev
                    )
                  }
                />
              ) : (
                <TextField
                  value={record.seller_code || "-"}
                />
              )
            }
          />
          <Table.Column
            dataIndex="seller_name"
            key="seller_name"
            title="Nome"
            render={(_, record: PartnerSeller) =>
              editingId ===
              (record._id ?? record.id ?? record.seller_code) ? (
                <Input
                  value={editingValues?.seller_name}
                  maxLength={MAX_SELLER_NAME_LENGTH}
                  onChange={(e) =>
                    setEditingValues((prev) =>
                      prev ? { ...prev, seller_name: e.target.value } : prev
                    )
                  }
                />
              ) : (
                <TextField
                  value={record.seller_name || "-"}
                />
              )
            }
          />
          <Table.Column<PartnerSeller>
            key="default_seller"
            title="Padrão"
            align="center"
            render={(_, record: PartnerSeller) => (
              <Radio
                aria-label={`Definir ${
                  record.seller_name || record.seller_code
                } como vendedor padrão`}
                checked={!!record.default_seller}
                disabled={isUpdatingSeller || !!record.default_seller}
                onChange={() => handleSetDefaultSeller(record)}
              />
            )}
          />
          <Table.Column
            dataIndex="health_insurer_name"
            key="health_insurer_name"
            title="Operadora"
            render={(_, record: PartnerSeller) => {
              const isEditing =
                editingId ===
                (record._id ?? record.id ?? record.seller_code);

              if (!isEditing) {
                return <TextField value={record.health_insurer_name || "-"} />;
              }

              return (
                <Select
                  {...healthInsurerSelectPropsTyped}
                  getPopupContainer={(trigger?: HTMLElement) =>
                    trigger?.parentElement || document.body
                  }
                  dropdownStyle={{ zIndex: 2200 }}
                  labelInValue
                  value={getEditingHealthInsurerValue(record)}
                  onChange={(option: { value: string; label: string }) => {
                    setEditingValues((prev) =>
                      prev
                        ? {
                            ...prev,
                            health_insurer_id: option.value,
                            health_insurer_name: option.label,
                          }
                        : prev
                    );
                  }}
                  style={{ minWidth: 160 }}
                  placeholder="Operadora"
                />
              );
            }}
          />
          <Table.Column<PartnerSeller>
            title="Ações"
            key="actions"
            render={(_, record: PartnerSeller) => {
              const recordKey = record._id ?? record.id ?? record.seller_code;
              const isEditing = editingId === recordKey;

              if (isEditing) {
                return (
                  <Space>
                    <Button
                      type="primary"
                      icon={<Icons.CheckOutlined />}
                      onClick={handleSaveEdit}
                      loading={isUpdatingSeller}
                    />
                    <Button
                      icon={<Icons.CloseOutlined />}
                      onClick={handleCancelEdit}
                      disabled={isUpdatingSeller}
                    />
                  </Space>
                );
              }

              const isActive = !!record.active;

              return (
                <Space>
                  <Button
                    size="small"
                    icon={
                      isActive ? <Icons.PlusOutlined /> : <Icons.MinusOutlined />
                    }
                    onClick={() => handleToggleSeller(record, !isActive)}
                    loading={isUpdatingSeller}
                  >
                    {isActive ? "On" : "Off"}
                  </Button>
                  <Button
                    icon={<Icons.FileTextOutlined />}
                    size="small"
                    title="Ver condições comerciais"
                    onClick={() => handleShowConditions(record)}
                  />
                  <Button
                    icon={<Icons.EditOutlined />}
                    size="small"
                    onClick={() => handleEdit(record)}
                  />
                  <Popconfirm
                    title="Excluir vendedor"
                    okText="Sim"
                    cancelText="Não"
                    onConfirm={() => handleDelete(record)}
                  >
                    <Button
                      icon={<Icons.DeleteOutlined />}
                      size="small"
                      danger
                      loading={isDeletingSeller}
                    />
                  </Popconfirm>
                </Space>
              );
            }}
          />
        </Table>
      </Modal>

      <Drawer
        placement="right"
        visible={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        zIndex={1005}
        width="100%"
        style={{ maxWidth: "575px" }}
        destroyOnClose
      >
        <Spin spinning={isCreating}>
          <Space direction="vertical" style={{ padding: "0 18px", width: "100%" }}>
          <AntdBreadcrumb>
            <AntdBreadcrumb.Item>
              <Icons.MenuOutlined style={{ fontSize: "12px" }} />
            </AntdBreadcrumb.Item>
            <AntdBreadcrumb.Item>Parceiro</AntdBreadcrumb.Item>
            <AntdBreadcrumb.Item>Parâmetros Compradores</AntdBreadcrumb.Item>
            <AntdBreadcrumb.Item>Novo Vendedor</AntdBreadcrumb.Item>
          </AntdBreadcrumb>

          <Typography.Title level={3} style={{ margin: "16px 0 8px" }}>
            Novo Vendedor
          </Typography.Title>

          <PartnerSellerForm
            form={createForm}
            healthInsurerSelectProps={healthInsurerSelectPropsTyped}
            loading={isCreating}
            onFinish={handleCreateSeller}
          />
          </Space>
        </Spin>
      </Drawer>

      <CommercialConditionsModal
        isOpen={!!conditionsSeller}
        sellerCode={conditionsSeller?.seller_code ?? null}
        sellerName={conditionsSeller?.seller_name}
        onClose={handleCloseConditions}
      />
    </>
  );
};
